'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAccountManager } from '@/hooks/use-account-manager';
import {
  useRxQuestionnaires,
  useRxSetProductOffering,
  type RxProductOffering,
} from '@/lib/api/rx';
import { QuestionnairePreview } from './questionnaire-preview';

type ProductRow = {
  id: string;
  name: string;
  image?: string | null;
};

export function ConfigureRxProductDialog({
  open,
  onOpenChange,
  product,
  offering,
  clinicalNetworkSlug,
  storeId,
  variantId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductRow | null;
  offering: RxProductOffering | null;
  clinicalNetworkSlug: string;
  storeId: string;
  variantId?: string | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const setOffering = useRxSetProductOffering();
  const questionnairesQuery = useRxQuestionnaires(storeId, { enabled: open });

  const [treatmentId, setTreatmentId] = React.useState('');
  const [questionnaireId, setQuestionnaireId] = React.useState('');
  const [refillQuestionnaireId, setRefillQuestionnaireId] = React.useState('');
  const [requiresLabs, setRequiresLabs] = React.useState(false);
  const [requiresIdVerification, setRequiresIdVerification] = React.useState(false);
  const [isDeaControlled, setIsDeaControlled] = React.useState(false);
  const [active, setActive] = React.useState(true);

  React.useEffect(() => {
    if (!open) return;
    setTreatmentId(offering?.externalOfferingId ?? '');
    setQuestionnaireId(offering?.externalQuestionnaireId ?? '');
    setRefillQuestionnaireId(offering?.externalRefillQuestionnaireId ?? '');
    setRequiresLabs(offering?.requiresLabs ?? false);
    setRequiresIdVerification(offering?.requiresIdVerification ?? false);
    setIsDeaControlled(offering?.isDeaControlled ?? false);
    setActive(offering?.active ?? true);
  }, [open, offering]);

  const handleSave = async () => {
    if (!product) return;
    if (!treatmentId.trim()) {
      toast({
        title: t('configureProductDialog.toasts.treatmentIdRequiredTitle'),
        description: t('configureProductDialog.toasts.treatmentIdRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }
    try {
      await setOffering.mutateAsync({
        productId: product.id,
        variantId: variantId ?? null,
        clinicalNetworkSlug,
        externalOfferingId: treatmentId.trim(),
        externalQuestionnaireId: questionnaireId.trim() || null,
        externalRefillQuestionnaireId: refillQuestionnaireId.trim() || null,
        requiresLabs,
        requiresIdVerification,
        isDeaControlled,
        active,
      });
      toast({
        title: t('configureProductDialog.toasts.productUpdatedTitle'),
        description: active
          ? t('configureProductDialog.toasts.productUpdatedActive')
          : t('configureProductDialog.toasts.productUpdatedInactive'),
      });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t('configureProductDialog.toasts.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('configureProductDialog.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('configureProductDialog.title')}</DialogTitle>
          <DialogDescription>
            {product
              ? t('configureProductDialog.descriptionWithName', { name: product.name })
              : t('configureProductDialog.descriptionFallback')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="rx-active"
              checked={active}
              onCheckedChange={(v) => setActive(Boolean(v))}
            />
            <Label htmlFor="rx-active" className="text-sm font-normal">
              {t('configureProductDialog.requiresConsultation')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rx-treatment-id">{t('configureProductDialog.treatmentIdLabel')}</Label>
            <Input
              id="rx-treatment-id"
              value={treatmentId}
              onChange={(e) => setTreatmentId(e.target.value)}
              placeholder={t('configureProductDialog.treatmentIdPlaceholder')}
              disabled={!isAccountManager && !!offering}
            />
            <p className="text-muted-foreground text-xs">
              {t('configureProductDialog.treatmentIdHint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rx-questionnaire">{t('configureProductDialog.intakeQuestionnaireLabel')}</Label>
            {questionnairesQuery.data?.questionnaires?.length ? (
              <select
                id="rx-questionnaire"
                value={questionnaireId}
                onChange={(e) => setQuestionnaireId(e.target.value)}
                className="border-input bg-background focus:ring-ring h-9 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-1"
              >
                <option value="">{t('configureProductDialog.intakeNoneOption')}</option>
                {questionnairesQuery.data.questionnaires.map((q) => (
                  <option key={q.externalQuestionnaireId} value={q.externalQuestionnaireId}>
                    {q.name}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="rx-questionnaire"
                value={questionnaireId}
                onChange={(e) => setQuestionnaireId(e.target.value)}
                placeholder={t('configureProductDialog.intakeQuestionnairePlaceholder')}
              />
            )}
            <p className="text-muted-foreground text-xs">
              {t('configureProductDialog.intakeQuestionnaireHint')}
            </p>
            {questionnaireId.trim() ? (
              <div className="max-h-56 overflow-y-auto pt-1">
                <QuestionnairePreview
                  storeId={storeId}
                  externalQuestionnaireId={questionnaireId.trim()}
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="rx-refill-questionnaire">{t('configureProductDialog.refillQuestionnaireLabel')}</Label>
            {questionnairesQuery.data?.questionnaires?.length ? (
              <select
                id="rx-refill-questionnaire"
                value={refillQuestionnaireId}
                onChange={(e) => setRefillQuestionnaireId(e.target.value)}
                className="border-input bg-background focus:ring-ring h-9 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-1"
              >
                <option value="">{t('configureProductDialog.refillNoneOption')}</option>
                {questionnairesQuery.data.questionnaires.map((q) => (
                  <option key={q.externalQuestionnaireId} value={q.externalQuestionnaireId}>
                    {q.name}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="rx-refill-questionnaire"
                value={refillQuestionnaireId}
                onChange={(e) => setRefillQuestionnaireId(e.target.value)}
                placeholder={t('configureProductDialog.refillQuestionnairePlaceholder')}
              />
            )}
            <p className="text-muted-foreground text-xs">
              {t('configureProductDialog.refillQuestionnaireHint')}
            </p>
            {refillQuestionnaireId.trim() ? (
              <div className="max-h-56 overflow-y-auto pt-1">
                <QuestionnairePreview
                  storeId={storeId}
                  externalQuestionnaireId={refillQuestionnaireId.trim()}
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="rx-labs"
                checked={requiresLabs}
                onCheckedChange={(v) => setRequiresLabs(Boolean(v))}
              />
              <Label htmlFor="rx-labs" className="text-sm font-normal">
                {t('configureProductDialog.requiresLabs')}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="rx-id"
                checked={requiresIdVerification}
                onCheckedChange={(v) => setRequiresIdVerification(Boolean(v))}
              />
              <Label htmlFor="rx-id" className="text-sm font-normal">
                {t('configureProductDialog.requiresIdVerification')}
              </Label>
            </div>
            {isAccountManager ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rx-dea"
                  checked={isDeaControlled}
                  onCheckedChange={(v) => setIsDeaControlled(Boolean(v))}
                />
                <Label htmlFor="rx-dea" className="text-sm font-normal">
                  {t('configureProductDialog.deaControlled')}
                </Label>
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('configureProductDialog.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={setOffering.isPending}>
            {setOffering.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('configureProductDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
